import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useForm from '@components/form/hooks/useForm';

import { updateSuperAdminUser } from '@actions/superAdminUsers';
import {
    getSuperAdminUserIsPosting,
    getSuperAdminUserPostSuccess,
    getSuperAdminUsersPostError,
} from '@selectors/superAdminUsers';

import { SuperAdminUser } from 'src/types/shared/SuperAdminUser';
import { site as siteEnum } from 'lib/src/shared/enums/site';
import useSite from 'lib/src/hooks/useSite';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';

export default function useUpdateSuperAdmin({
    id,
    email,
    firstName,
    lastName,
    site,
}: SuperAdminUser): UpdateSuperAdminReturn {
    const dispatch = useDispatch();

    const [formState, handleChange] = useForm({ email, firstName, lastName, site });

    const handleSubmit = useCallback(() => {
        dispatch(updateSuperAdminUser(id, formState));
    }, [dispatch, id, formState]);

    const isPosting = useSelector(getSuperAdminUserIsPosting);
    const error = useSelector(getSuperAdminUsersPostError);
    const postSuccess = useSelector(getSuperAdminUserPostSuccess);

    const userSite = useSite();
    const siteOptions: DropdownOption<siteEnum>[] = [
        { label: 'All', value: siteEnum.ALL },
        { label: 'IX', value: siteEnum.IX },
        { label: 'GA', value: siteEnum.GA },
    ];
    const shouldShowSiteOptions = userSite === siteEnum.ALL;

    return {
        handleSubmit,
        formState,
        handleChange,
        isPosting,
        error,
        postSuccess,
        siteOptions,
        shouldShowSiteOptions,
    };
}

interface FormState {
    email: string;
    firstName: string;
    lastName: string;
    site: siteEnum;
}
interface UpdateSuperAdminReturn {
    handleSubmit: () => void;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    isPosting: boolean;
    error: string | null;
    postSuccess: boolean;
    siteOptions: DropdownOption<siteEnum>[];
    shouldShowSiteOptions: boolean;
}
